import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { Bell, Check, CheckCheck, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Bodies longer than this get a "Read more" expander (Phase 6).
const EXPAND_THRESHOLD = 90;

export default function NotificationsBell() {
  const { user, setUser } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [expanded, setExpanded] = useState(() => new Set());
  const nav = useNavigate();

  const load = () => api.notifications().then((d) => { setItems(d.items); setUnread(d.unread); }).catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const open = async (n) => {
    if (!n.read) {
      await api.markRead(n.id);
      load();
    }
    if (n.link) {
      try {
        const u = new URL(n.link);
        nav(u.pathname + (u.search || ""));
      } catch {
        nav(n.link);
      }
    }
  };

  const toggleExpand = async (e, n) => {
    e.stopPropagation();
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(n.id) ? next.delete(n.id) : next.add(n.id);
      return next;
    });
    if (!n.read) {
      await api.markRead(n.id);
      load();
    }
  };

  const markAll = async () => { await api.markAllRead(); load(); };

  const emailsOn = user ? !user.email_opt_out : true;
  const toggleEmails = async (on) => {
    try {
      const r = await api.setNotificationPrefs(!on);
      setUser({ ...user, email_opt_out: r.email_opt_out });
      toast.success(on ? "Email notifications on" : "Email notifications off — in-app alerts stay on");
    } catch {
      toast.error("Could not update preference");
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="notifications-bell" aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"} className="relative hover:opacity-90 p-2 rounded-full hover:bg-forest/5">
          <Bell className="w-5 h-5 text-forest" />
          {unread > 0 && (
            <span data-testid="notifications-unread" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-terracotta text-white text-[10px] font-semibold flex items-center justify-center px-1">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-[440px] overflow-y-auto">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {unread > 0 && (
            <button data-testid="mark-all-read" onClick={markAll} className="text-xs text-forest hover:underline inline-flex items-center gap-1">
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length === 0 && <div className="px-4 py-6 text-sm text-neutral-500 text-center">You're all caught up.</div>}
        {items.map((n) => {
          const isExpanded = expanded.has(n.id);
          const expandable = (n.body || "").length > EXPAND_THRESHOLD;
          return (
            <div
              key={n.id}
              data-testid={`notif-${n.id}`}
              onClick={() => open(n)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && open(n)}
              className={`w-full text-left px-3 py-3 hover:bg-neutral-50 border-b border-neutral-100 cursor-pointer ${n.read ? "" : "bg-forest/5"}`}
            >
              <div className="flex items-start gap-2">
                {!n.read && <span className="w-2 h-2 rounded-full bg-terracotta mt-1.5 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-forest">{n.title}</div>
                  <div className={`text-xs text-neutral-600 mt-0.5 whitespace-pre-line ${isExpanded ? "" : "line-clamp-2"}`}>{n.body}</div>
                  {n.cta && n.link && (
                    <button
                      data-testid={`notif-cta-${n.id}`}
                      onClick={(e) => { e.stopPropagation(); open(n); }}
                      className="mt-2 inline-flex items-center rounded-full bg-forest text-white text-[11px] font-medium px-3 py-1 hover:bg-forest-hover"
                    >
                      {n.cta}
                    </button>
                  )}
                  <div className="flex items-center justify-between mt-1.5">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-400">{new Date(n.created_at).toLocaleString()}</div>
                    {expandable && (
                      <button
                        data-testid={`notif-expand-${n.id}`}
                        onClick={(e) => toggleExpand(e, n)}
                        className="text-[11px] text-forest font-medium hover:underline inline-flex items-center gap-0.5"
                      >
                        {isExpanded ? <>Show less <ChevronUp className="w-3 h-3" /></> : <>Read more <ChevronDown className="w-3 h-3" /></>}
                      </button>
                    )}
                  </div>
                </div>
                {n.read && <Check className="w-4 h-4 text-neutral-300 shrink-0" />}
              </div>
            </div>
          );
        })}
        {user && (
          <>
            <DropdownMenuSeparator />
            <div data-testid="notif-settings" className="px-3 py-2.5 flex items-center justify-between gap-3">
              <div>
                <div className="text-xs font-medium text-forest">Email notifications</div>
                <div className="text-[11px] text-neutral-500">Alerts, messages & digests by email. In-app stays on.</div>
              </div>
              <Switch data-testid="notif-email-toggle" checked={emailsOn} onCheckedChange={toggleEmails} />
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
